// Website entry point: landing form → /login?website= → magic link → business basics prefill →
// automatic first profile draft. Pure helpers; `@lobby/shared/website` keeps zod out of the main chunk.
import { businessNameFromWebsite, normalizeWebsiteInput } from '@lobby/shared/website'

export { businessNameFromWebsite, normalizeWebsiteInput }

/** Same-browser fallback for the website, in case the server copy is missing. */
export const PENDING_WEBSITE_KEY = 'lobby.pendingWebsite'

type KeyValue = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const local = (): KeyValue | null => {
  try {
    return window.localStorage
  } catch {
    return null
  }
}
const session = (): KeyValue | null => {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

export function readPendingWebsite(store: KeyValue | null = local()): string | null {
  try {
    const v = store?.getItem(PENDING_WEBSITE_KEY)
    return v ? normalizeWebsiteInput(v) : null
  } catch {
    return null
  }
}

export function storePendingWebsite(url: string, store: KeyValue | null = local()): void {
  try {
    store?.setItem(PENDING_WEBSITE_KEY, url)
  } catch {
    /* private mode or storage full: the server copy still works */
  }
}

export function clearPendingWebsite(store: KeyValue | null = local()): void {
  try {
    store?.removeItem(PENDING_WEBSITE_KEY)
  } catch {
    /* ignore */
  }
}

/** `https://www.praxis-weber.de/` → `www.praxis-weber.de`. */
export const websiteHost = (url: string): string => new URL(url).hostname

/** What the website field shows: no scheme, no trailing slash (`https://praxis-weber.de/` → `praxis-weber.de`). */
export const displayWebsite = (url: string): string => url.replace(/^https?:\/\//i, '').replace(/\/$/, '')

/** Where the landing form sends the visitor, or the inline error to show. */
export function landingTarget(input: string): { to: string } | { error: string } {
  if (!input.trim()) return { to: '/login' }
  const url = normalizeWebsiteInput(input)
  if (!url) return { error: 'Enter your website address, for example yourbusiness.com' }
  return { to: `/login?website=${encodeURIComponent(url)}` }
}

/** The website business basics starts with: the server's pending website, else the same-browser copy. */
export function initialWebsite(pendingWebsite: string | null | undefined, stored: string | null): string {
  const url = (pendingWebsite && normalizeWebsiteInput(pendingWebsite)) || (stored && normalizeWebsiteInput(stored)) || null
  return url ? displayWebsite(url) : ''
}

/** The business name follows the website field until the owner edits the name. */
export function suggestedName(website: string, nameTouched: boolean, currentName: string): string {
  return nameTouched ? currentName : businessNameFromWebsite(website)
}

// ---------- Automatic first profile draft (setup step b) ----------

/** Client IDs whose automatic draft already started in this tab. Survives StrictMode remounts and step changes. */
const autoStarted = new Set<string>()
const autoKey = (clientId: string) => `lobby.autoGenerated.${clientId}`

/**
 * Claims the one automatic generation for a client. True only the first time (per tab, and per browser
 * session via sessionStorage), so a failed draft is never retried by itself; the button stays for that.
 */
export function claimAutoGenerate(clientId: string, memory: Set<string> = autoStarted, store: KeyValue | null = session()): boolean {
  if (memory.has(clientId)) return false
  try {
    if (store?.getItem(autoKey(clientId))) {
      memory.add(clientId)
      return false
    }
    store?.setItem(autoKey(clientId), '1')
  } catch {
    /* in-memory guard still applies */
  }
  memory.add(clientId)
  return true
}

/** Start the first draft by itself only when the profile is loaded, was never generated and nothing is running or edited. */
export function shouldAutoGenerate(s: { enabled: boolean; loaded: boolean; neverGenerated: boolean; dirty: boolean; busy: boolean }): boolean {
  return s.enabled && s.loaded && s.neverGenerated && !s.dirty && !s.busy
}
